/** The diagnosis in words: light states, a row's summary, what each action asks before it runs. Pure. */
import type { ActionAudit, ActionKey, ActionView, Diagnosis, Light, LightState } from './schemas'

/** What a light's state reads as, for screen readers and the panel. Colour never says it alone. */
export const STATE_WORDS: Record<LightState, string> = { ok: 'ok', warn: 'warning', down: 'down', unknown: 'unknown' }

const RANK: Record<LightState, number> = { down: 0, warn: 1, unknown: 2, ok: 3 }

/** "2 down · 1 warning", or "all ok" / "nothing reported" when there's nothing to flag. */
export function lightsSummary(lights: Light[]): string {
  if (!lights.length) return 'no signals'
  const n = (s: LightState) => lights.filter((l) => l.state === s).length
  const parts = [n('down') ? `${n('down')} down` : '', n('warn') ? `${n('warn')} warning${n('warn') === 1 ? '' : 's'}` : ''].filter(Boolean)
  if (parts.length) return parts.join(' · ')
  return n('ok') === lights.length ? 'all ok' : `${n('ok')} ok · ${n('unknown')} unknown`
}

/** The worst state among the lights: what a row's attention mark follows. */
export function worstState(lights: Light[]): LightState | null {
  return lights.length ? [...lights].sort((a, b) => RANK[a.state] - RANK[b.state])[0]!.state : null
}

/** Down first, then warnings, then the rest, in the API's order within each. */
export function problemsFirst(lights: Light[]): Light[] {
  return [...lights].sort((a, b) => RANK[a.state] - RANK[b.state])
}

/** The question each action asks before it runs: what happens, in a sentence. */
export function confirmWords(view: Pick<ActionView, 'key' | 'label'>, founder: string): { question: string; yes: string } {
  const copy: Record<ActionKey, { question: string; yes: string }> = {
    restartBox:
      view.label === 'Power on the box'
        ? { question: `Power on ${founder}'s box? The agent reconnects a minute or two after it boots.`, yes: 'Power on' }
        : { question: `Reboot ${founder}'s box? The agent, Paseo and the preview drop for a minute or two.`, yes: 'Reboot' },
    restartAgent: { question: 'Restart the agent? It reconnects within seconds; what the founder runs keeps running.', yes: 'Restart agent' },
    restartPaseo: { question: "Restart Paseo? The founder's phone reconnects, and agent sessions running in Paseo stop.", yes: 'Restart Paseo' },
    restartPreview: { question: 'Restart the preview? The preview address drops for a few seconds.', yes: 'Restart preview' },
    redeployApp: { question: 'Redeploy the app? Railway builds the current version again; the address keeps serving until it is live.', yes: 'Redeploy' },
  }
  return copy[view.key]
}

/** "Restart the box", "Power on the box": the action's own label, from the actions list. */
export function actionLabel(d: Pick<Diagnosis, 'actions'> | undefined, key: string): string {
  return d?.actions.find((a) => a.key === key)?.label ?? key
}

const ACTION_DONE: Record<string, string> = {
  restartBox: 'Restarted the box',
  restartAgent: 'Restarted the agent',
  restartPaseo: 'Restarted Paseo',
  restartPreview: 'Restarted the preview',
  redeployApp: 'Redeployed the app',
}

/** One audit line: "Restarted Paseo · johnnie@cpg.computer" with the outcome. */
export function auditWords(a: ActionAudit): string {
  const what = ACTION_DONE[a.action] ?? a.action
  return a.outcome === 'ok' ? `${what} · ${a.actor}` : `${what} failed · ${a.actor}`
}
